import React, { useState } from "react";
import {
    Form,
    Input,
    Button,
    Select,
    Row,
    Col,
    Card,
    Table,
    DatePicker,
    InputNumber,
    Space,
    Typography,
} from "antd";
import { ChevronLeft, Plus, Trash2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

const { Title, Text } = Typography;
const { Option } = Select;

const InvoiceAddEdit = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = !!id;
    const [form] = Form.useForm();
    const [items, setItems] = useState([
        {
            key: '1',
            productId: '1',
            productName: 'Product Name',
            quantity: 1,
            price: 100,
            total: 100,
        },
    ]);

    const handleAddItem = () => {
        const newItem = {
            key: String(items.length + 1),
            productId: '',
            productName: '',
            quantity: 1,
            price: 0,
            total: 0,
        };
        setItems([...items, newItem]);
    };

    const handleRemoveItem = (key) => {
        setItems(items.filter(item => item.key !== key));
    };

    const handleItemChange = (key, field, value) => {
        const updatedItems = items.map(item => {
            if (item.key === key) {
                const updated = { ...item, [field]: value };
                if (field === 'quantity' || field === 'price') {
                    updated.total = updated.quantity * updated.price;
                }
                return updated;
            }
            return item;
        });
        setItems(updatedItems);
    };

    const itemColumns = [
        {
            title: 'Product',
            dataIndex: 'productName',
            key: 'productName',
            width: '30%',
            render: (text, record) => (
                <Input
                    value={record.productName}
                    onChange={(e) => handleItemChange(record.key, 'productName', e.target.value)}
                    placeholder="Product name"
                />
            ),
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            width: '15%',
            render: (text, record) => (
                <InputNumber
                    value={record.quantity}
                    onChange={(value) => handleItemChange(record.key, 'quantity', value)}
                    min={1}
                />
            ),
        },
        {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            width: '15%',
            render: (text, record) => (
                <InputNumber
                    value={record.price}
                    onChange={(value) => handleItemChange(record.key, 'price', value)}
                    min={0}
                    formatter={(value) => `$${value}`}
                    parser={(value) => value.replace('$', '')}
                />
            ),
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            width: '15%',
            render: (text, record) => (
                <Text className="invoice-item-name">${record.total}</Text>
            ),
        },
        {
            title: 'Action',
            key: 'action',
            width: '10%',
            render: (_, record) => (
                <Button
                    type="text"
                    danger
                    icon={<Trash2 size={16} />}
                    onClick={() => handleRemoveItem(record.key)}
                />
            ),
        },
    ];

    const onFinish = (values) => {
        const formData = {
            ...values,
            items: items,
        };
        console.log('Form Data:', formData);
        // API call would go here
        navigate('/sales/invoices');
    };

    return (
        <div className="invoice-details-container">
            {/* Header */}
            <div className="invoice-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Button
                        type="text"
                        icon={<ChevronLeft size={20} />}
                        onClick={() => navigate(-1)}
                        style={{ padding: 0 }}
                    />
                    <Title level={4} className="invoice-title">
                        {isEditMode ? `Edit Invoice #${id}` : 'Create New Invoice'}
                    </Title>
                </div>
                <Space>
                    <Button onClick={() => navigate(-1)}>Cancel</Button>
                    <Button type="primary" onClick={() => form.submit()}>
                        {isEditMode ? 'Update Invoice' : 'Create Invoice'}
                    </Button>
                </Space>
            </div>

            <Row gutter={24}>
                {/* Left Column - Invoice Form */}
                <Col span={16}>
                    <Card className="invoice-card" bordered>
                        <Form form={form} layout="vertical" onFinish={onFinish}>
                            {/* Invoice Details */}
                            <div style={{ marginBottom: '24px' }}>
                                <Title level={5} style={{ marginBottom: '16px' }}>Invoice Details</Title>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Invoice Number"
                                            name="invoiceNumber"
                                            rules={[{ required: true, message: 'Please enter invoice number' }]}
                                        >
                                            <Input placeholder="INV-001" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Order ID"
                                            name="orderId"
                                            rules={[{ required: true, message: 'Please enter order ID' }]}
                                        >
                                            <Input placeholder="ORD-123" />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Invoice Date"
                                            name="invoiceDate"
                                            rules={[{ required: true, message: 'Please select invoice date' }]}
                                        >
                                            <DatePicker style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Due Date"
                                            name="dueDate"
                                            rules={[{ required: true, message: 'Please select due date' }]}
                                        >
                                            <DatePicker style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>

                            {/* Customer Details */}
                            <div style={{ marginBottom: '24px' }}>
                                <Title level={5} style={{ marginBottom: '16px' }}>Customer Details</Title>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Customer Name"
                                            name="customerName"
                                            rules={[{ required: true, message: 'Please enter customer name' }]}
                                        >
                                            <Input placeholder="John Doe" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Email"
                                            name="email"
                                            rules={[
                                                { required: true, message: 'Please enter email' },
                                                { type: 'email', message: 'Please enter valid email' },
                                            ]}
                                        >
                                            <Input type="email" placeholder="john@example.com" />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Phone"
                                            name="phone"
                                            rules={[{ required: true, message: 'Please enter phone number' }]}
                                        >
                                            <Input placeholder="+1 234 567 8900" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Company"
                                            name="company"
                                        >
                                            <Input placeholder="Company Name" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>

                            {/* Billing Address */}
                            <div style={{ marginBottom: '24px' }}>
                                <Title level={5} style={{ marginBottom: '16px' }}>Billing Address</Title>
                                <Form.Item
                                    label="Street Address"
                                    name="billingStreet"
                                    rules={[{ required: true, message: 'Please enter street address' }]}
                                >
                                    <Input placeholder="123 Main Street" />
                                </Form.Item>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            label="City"
                                            name="billingCity"
                                            rules={[{ required: true, message: 'Please enter city' }]}
                                        >
                                            <Input placeholder="New York" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="State/Province"
                                            name="billingState"
                                        >
                                            <Input placeholder="NY" />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Postal Code"
                                            name="billingPostal"
                                        >
                                            <Input placeholder="10001" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Country"
                                            name="billingCountry"
                                        >
                                            <Input placeholder="United States" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>

                            {/* Shipping Address */}
                            <div style={{ marginBottom: '24px' }}>
                                <Title level={5} style={{ marginBottom: '16px' }}>Shipping Address</Title>
                                <Form.Item
                                    label="Street Address"
                                    name="shippingStreet"
                                    rules={[{ required: true, message: 'Please enter street address' }]}
                                >
                                    <Input placeholder="123 Main Street" />
                                </Form.Item>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            label="City"
                                            name="shippingCity"
                                            rules={[{ required: true, message: 'Please enter city' }]}
                                        >
                                            <Input placeholder="New York" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="State/Province"
                                            name="shippingState"
                                        >
                                            <Input placeholder="NY" />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Postal Code"
                                            name="shippingPostal"
                                        >
                                            <Input placeholder="10001" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            label="Country"
                                            name="shippingCountry"
                                        >
                                            <Input placeholder="United States" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>

                            {/* Items Section */}
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                    <Title level={5} style={{ margin: 0 }}>Invoice Items</Title>
                                    <Button
                                        type="primary"
                                        icon={<Plus size={16} />}
                                        onClick={handleAddItem}
                                    >
                                        Add Item
                                    </Button>
                                </div>
                                <Table scroll={{ x: 'max-content' }}
                                    columns={itemColumns}
                                    dataSource={items}
                                    pagination={false}
                                    className="invoices-table"
                                />
                            </div>
                        </Form>
                    </Card>
                </Col>

                {/* Right Column - Summary */}
                <Col span={8}>
                    <Card className="invoice-info-card" bordered>
                        <Title level={5} style={{ marginBottom: '16px' }}>Invoice Summary</Title>

                        <div className="invoice-address-section">
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                                <Text className="invoice-info-title">Subtotal</Text>
                                <Text className="invoice-item-name">
                                    ${items.reduce((sum, item) => sum + (item.quantity * item.price), 0).toFixed(2)}
                                </Text>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                                <Text className="invoice-info-title">Tax (10%)</Text>
                                <Text className="invoice-item-name">
                                    ${(items.reduce((sum, item) => sum + (item.quantity * item.price), 0) * 0.1).toFixed(2)}
                                </Text>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                                <Text className="invoice-info-title">Shipping</Text>
                                <Text className="invoice-item-name">$0.00</Text>
                            </div>
                        </div>

                        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '12px', marginBottom: '24px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text className="invoice-item-name">Grand Total</Text>
                                <Text style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                                    ${(items.reduce((sum, item) => sum + (item.quantity * item.price), 0) * 1.1).toFixed(2)}
                                </Text>
                            </div>
                        </div>

                        <Form.Item
                            label="Payment Status"
                            name="paymentStatus"
                            rules={[{ required: true, message: 'Please select payment status' }]}
                        >
                            <Select placeholder="Select status">
                                <Option value="paid">Paid</Option>
                                <Option value="pending">Pending</Option>
                                <Option value="overdue">Overdue</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item
                            label="Notes"
                            name="notes"
                        >
                            <Input.TextArea
                                rows={4}
                                placeholder="Add any additional notes..."
                            />
                        </Form.Item>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default InvoiceAddEdit;
